import type { TFunction } from 'i18next'
import type {
    DataTableAction,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import { Eye, Pause, Pencil, Play, Trash2 } from 'lucide-react'
import type { DataAccessAssignment, ResourceGroup } from '../../types'

interface BuildColumnsArgs {
    t: TFunction
    lang: string
    resourceGroups: ResourceGroup[]
}

/**
 * Look up a resource label from the catalog by keycloak_name.
 * Falls back to the raw key if not found in catalog.
 */
const resolveResourceLabel = (
    keycloakName: string,
    groups: ResourceGroup[],
    lang: string
): string => {
    for (const group of groups) {
        const found = group.resources.find((r) => r.key === keycloakName)
        if (found) return lang === 'vi' ? found.label_vi : found.label_en
    }
    return keycloakName || '—'
}

/** Build the apply summary string mirroring previous RuleCard logic */
const buildApplySummary = (row: DataAccessAssignment, t: TFunction): string => {
    if (row.apply === 'companies') return t('data-access-apply-whole-company')
    const deps = row.departments?.length ?? 0
    const teams = row.teammates?.length ?? 0
    const ass = row.assignees?.length ?? 0
    if (deps > 0) return t('data-access-apply-departments', { count: deps })
    if (teams > 0) return t('data-access-apply-teammates', { count: teams })
    if (ass > 0) return t('data-access-apply-assignees', { count: ass })
    return t('data-access-apply-none')
}

export const buildDataAccessColumns = ({
    t,
    lang,
    resourceGroups,
}: BuildColumnsArgs): DataTableColumn<DataAccessAssignment>[] => [
    {
        key: 'name',
        title: t('name'),
        width: 280,
        cellClassName: 'truncate',
        render: (_v, row) => (
            <div className="flex flex-col min-w-0">
                <span className="font-medium truncate">{row.name}</span>
                {row.description && (
                    <span className="text-xs text-muted-foreground truncate">
                        {row.description}
                    </span>
                )}
            </div>
        ),
    },
    {
        key: 'keycloak_name',
        title: t('data-access-resource'),
        width: 180,
        cellClassName: 'truncate',
        render: (_v, row) =>
            resolveResourceLabel(row.keycloak_name, resourceGroups, lang),
    },
    {
        key: 'apply',
        title: t('data-access-apply-label'),
        width: 220,
        cellClassName: 'truncate text-xs text-muted-foreground',
        render: (_v, row) => buildApplySummary(row, t),
    },
    {
        key: 'reject_columns',
        title: t('data-access-hidden-columns'),
        width: 120,
        cellClassName: 'text-center truncate',
        render: (_v, row) => row.reject_columns?.length ?? 0,
    },
    {
        key: 'is_active',
        title: t('status'),
        width: 110,
        cellClassName: 'text-center truncate',
        formatter: {
            type: 'options',
            options: [
                { value: true, label: t('active'), color: 'success' },
                { value: false, label: t('inactive'), color: 'secondary' },
            ],
            showDot: true,
            variant: 'dot',
        },
    },
    {
        key: 'updated_at',
        title: t('updated-at'),
        width: 130,
        cellClassName: 'truncate',
        formatter: { type: 'date', format: 'DD/MM/YYYY' },
    },
]

interface BuildActionsArgs {
    t: TFunction
    onEdit: (row: DataAccessAssignment) => void
    onTest: (row: DataAccessAssignment) => void
    onToggle: (row: DataAccessAssignment) => void
    onDelete: (row: DataAccessAssignment) => void
}

export const buildDataAccessActions = ({
    t,
    onEdit,
    onTest,
    onToggle,
    onDelete,
}: BuildActionsArgs): DataTableAction<DataAccessAssignment>[] => [
    {
        key: 'edit',
        label: t('edit'),
        icon: <Pencil className="h-4 w-4" />,
        onClick: onEdit,
    },
    {
        key: 'test',
        label: t('data-access-test-tooltip'),
        icon: <Eye className="h-4 w-4" />,
        onClick: onTest,
    },
    {
        key: 'activate',
        label: t('data-access-toggle-on-confirm'),
        icon: <Play className="h-4 w-4" />,
        variant: 'success',
        onClick: onToggle,
        hidden: (row) => row.is_active,
    },
    {
        key: 'deactivate',
        label: t('data-access-toggle-off-confirm'),
        icon: <Pause className="h-4 w-4" />,
        variant: 'warning',
        onClick: onToggle,
        hidden: (row) => !row.is_active,
    },
    {
        key: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-4 w-4" />,
        variant: 'danger',
        onClick: onDelete,
    },
]
